import { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Alert, CircularProgress, Typography, Box,
  FormGroup, FormControlLabel, Checkbox,
} from '@mui/material';
import type { Study, PatientBean, StorageNote, ObjectTypeForDeletion } from '../../api/types';
import { api } from '../../api/rpc';

const ALL_OBJECT_TYPES: ObjectTypeForDeletion[] = [
  'IMAGE', 'REPORT', 'ATTACHMENT', 'DICTATION', 'WAVEFORM',
  'REGISTRATION', 'KEY_IMAGE', 'ENCAPSULATED_DOCUMENT',
  'PRESENTATION_STATE', 'STRUCTURED_REPORT',
];

interface StorageOptionsDialogProps {
  open: boolean;
  study: Study | null;
  onClose: () => void;
  onComplete: () => void;
}

export const StorageOptionsDialog = ({ open, study, onClose, onComplete }: StorageOptionsDialogProps) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [existingNote, setExistingNote] = useState<StorageNote | null>(null);
  const [forbiddenTypes, setForbiddenTypes] = useState<Set<ObjectTypeForDeletion>>(new Set());
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (!open || !study) return;
    setLoading(true);
    setError(null);

    const patKey = study.patientKey ?? study.objKey;
    api.getPatientBean(patKey, true)
      .then((_pb: PatientBean) => {
        // Find existing storage note for this study
        const note = _pb.storageNotes?.find(n =>
          n.objKey === study.objKey || n.level === 'STUDY',
        );
        setExistingNote(note ?? null);
        if (note?.forbiddenForDeletion) {
          setForbiddenTypes(new Set(note.forbiddenForDeletion as ObjectTypeForDeletion[]));
        } else {
          setForbiddenTypes(new Set());
        }
        setReason(note?.reason ?? '');
      })
      .catch(e => setError(e instanceof Error ? e.message : 'Failed to load storage options'))
      .finally(() => setLoading(false));
  }, [open, study]);

  const handleToggle = (t: ObjectTypeForDeletion) => {
    setForbiddenTypes(prev => {
      const next = new Set(prev);
      if (next.has(t)) next.delete(t);
      else next.add(t);
      return next;
    });
  };

  const handleSave = async () => {
    if (!study) return;
    setSaving(true);
    setError(null);
    try {
      await api.saveStorageOptions({
        itemObjKey: study.objKey,
        level: 'STUDY',
        reason: reason || undefined,
        forbiddenTypes: [...forbiddenTypes],
      });
      onComplete();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save storage options');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Storage Options</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {study && (
          <Box sx={{ mb: 2, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Typography variant="body2">
              <strong>{study.name}</strong> — {study.acc}
            </Typography>
          </Box>
        )}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Protect from Deletion
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Check the object types that should be protected from automatic deletion for this study.
            </Typography>
            <FormGroup>
              {ALL_OBJECT_TYPES.map(t => (
                <FormControlLabel
                  key={t}
                  control={
                    <Checkbox
                      checked={forbiddenTypes.has(t)}
                      onChange={() => handleToggle(t)}
                      size="small"
                    />
                  }
                  label={t.replace(/_/g, ' ')}
                />
              ))}
            </FormGroup>
            <TextField
              label="Reason"
              value={reason}
              onChange={e => setReason(e.target.value)}
              fullWidth multiline rows={2} size="small"
              sx={{ mt: 2 }}
            />
            {existingNote && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Last updated by {existingNote.author ?? 'unknown'}
                {existingNote.date ? ` on ${new Date(existingNote.date).toLocaleDateString()}` : ''}
              </Typography>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={loading || saving}>
          {saving ? <CircularProgress size={20} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
